import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Spinner from "../Spinner";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../lds/LdsTable";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";

const DEFAULT_LIMIT = 500;

const safeString = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

const renderValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    const preview = value.slice(0, 3).map((v) => safeString(v)).join(", ");
    return value.length > 3 ? `${preview} …` : preview;
  }
  return "…";
};

const pickItems = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  const items = data.items || data.data || data.results || data.content || [];
  return Array.isArray(items) ? items : [];
};

const FilterChip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
      active
        ? "bg-indigo-600 text-white shadow shadow-indigo-500/25 dark:bg-indigo-500"
        : "border border-slate-200/70 bg-white/70 text-slate-700 hover:bg-white dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
    }`}
  >
    {children}
  </button>
);

const statusTheme = (status) => {
  const s = toStatus(status);
  if (s === "valid") {
    return {
      pill: "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400",
      badge: "border-emerald-200/70 bg-emerald-50/70 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/15 dark:text-emerald-100",
    };
  }
  if (s === "invalid") {
    return {
      pill: "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400",
      badge: "border-red-200/70 bg-red-50/70 text-red-900 dark:border-red-500/30 dark:bg-red-950/15 dark:text-red-100",
    };
  }
  if (s === "suspicious") {
    return {
      pill: "bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400",
      badge: "border-amber-200/70 bg-amber-50/70 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/15 dark:text-amber-50",
    };
  }
  // pending (default)
  return {
    pill: "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400",
    badge: "border-indigo-200/70 bg-indigo-50/70 text-indigo-900 dark:border-indigo-500/30 dark:bg-indigo-950/15 dark:text-indigo-100",
  };
};

const StatusPill = ({ label, status, onRemove }) => {
  const theme = statusTheme(status);
  return (
  <button
    type="button"
    onClick={onRemove}
    className={`group inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold text-white shadow shadow-indigo-500/25 transition ${theme.pill}`}
    title={label}
  >
    <span>{label}</span>
    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/15 text-[12px] leading-none transition group-hover:bg-white/20">
      ×
    </span>
  </button>
  );
};

const StatusBadge = ({ label, status }) => {
  const theme = statusTheme(status);
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${theme.badge}`}>
      {label}
    </span>
  );
};

const pickFirst = (...candidates) => {
  for (const v of candidates) {
    if (v == null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    return v;
  }
  return null;
};

const toStatus = (v) => String(v || "").trim().toLowerCase();

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number" && Number.isFinite(value)) {
    const abs = Math.abs(value);
    const ms =
      abs >= 1e17 ? Math.floor(value / 1_000_000) :
      abs >= 1e14 ? Math.floor(value / 1_000) :
      abs >= 1e12 ? Math.floor(value) :
      abs >= 1e9 ? Math.floor(value * 1_000) :
      Math.floor(value);
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (s && /^-?\\d+(\\.\\d+)?$/.test(s)) {
      const n = Number(s);
      if (Number.isFinite(n)) return toDate(n);
    }
  }
  if (Array.isArray(value)) {
    const [y, m, d, hh = 0, mm = 0, ss = 0, sub = 0] = value;
    if (!y || !m || !d) return null;
    const ms = typeof sub === "number" ? (sub > 1000 ? Math.floor(sub / 1_000_000) : Math.floor(sub)) : 0;
    const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeAttempt = (item, fallback = {}) => {
  const email = pickFirst(item?.email, item?.currentEmail, item?.current_email, item?.parentEmail, item?.parent_email);
  const suggestedEmail = pickFirst(item?.suggestedEmail, item?.suggested_email);
  const parentId = pickFirst(item?.parentId, item?.parent_id, item?.parentUUID, item?.parentUuid, item?.parent_uuid);
  const joinedName = [pickFirst(item?.firstName, item?.first_name), pickFirst(item?.lastName, item?.last_name)]
    .filter(Boolean)
    .map((v) => String(v).trim())
    .filter(Boolean)
    .join(" ");
  const fullName =
    pickFirst(
      item?.fullName,
      item?.full_name,
      item?.displayName,
      item?.display_name,
      item?.name,
      item?.parentName,
      item?.parent_name,
      item?.parentFullName,
      item?.parent_full_name,
      item?.teacherName,
      item?.teacher_name,
      item?.teacherFullName,
      item?.teacher_full_name,
      item?.personName,
      item?.person_name
    ) ||
    joinedName ||
    "";
  const status = toStatus(pickFirst(item?.status, item?.state, fallback.status));
  const reasons = pickFirst(item?.reasons, item?.reason, item?.messages, item?.message, fallback.reasons);
  const checkedAt = pickFirst(item?.checkedAt, item?.checked_at, item?.validatedAt, item?.validated_at, item?.createdAt, item?.created_at);
  return {
    email: email != null ? String(email) : "",
    fullName: fullName != null ? String(fullName) : "",
    parentId: parentId != null && parentId !== "" ? String(parentId) : "",
    suggestedEmail: suggestedEmail != null ? String(suggestedEmail) : "",
    status,
    reasons,
    checkedAt,
    checkedAtDate: toDate(checkedAt),
    kind: fallback.kind || null, // not_ok | current | pending
    _raw: item,
  };
};

const DISPLAY_COLUMNS = ["email", "fullName", "suggestedEmail", "status", "reasons", "checkedAt"];

const normalizeReasonList = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).map((v) => v.trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return [];
    if (raw.includes(",")) return raw.split(",").map((s) => s.trim()).filter(Boolean);
    return [raw];
  }
  return [safeString(value)];
};

const humanizeReason = (reason) => {
  const r = String(reason || "").trim();
  if (!r) return "";
  return r.replace(/_/g, " ");
};

/**
 * Reusable panel for email-validation result exploration.
 *
 * Endpoints (relative to /api/v1):
 * - GET /admin/email-validation/not-ok?limit=500
 * - GET /admin/email-validation/current?limit=500&status=invalid|valid|suspicious
 * - GET /admin/email-validation/pending
 */
const EmailValidationChecksPanel = ({
  className = "",
  limit = DEFAULT_LIMIT,
  embedded = true,
  refreshToken = 0,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [selectedStatuses, setSelectedStatuses] = useState([]); // multi: valid|invalid|suspicious|pending
  const [query, setQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [rows, setRows] = useState([]);
  const [teacherEmailSet, setTeacherEmailSet] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);

  const locale = useMemo(() => {
    const lang = String(i18n?.language || "").toLowerCase();
    if (lang.startsWith("nl")) return "nl-NL";
    return "en-US";
  }, [i18n?.language]);

  const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (Array.isArray(value)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0, sub = 0] = value;
      if (!y || !m || !d) return null;
      const ms =
        typeof sub === "number"
          ? sub > 1000 ? Math.floor(sub / 1_000_000) : Math.floor(sub)
          : 0;
      const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const d = toDate(value);
    if (!d) return "—";
    return d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  };

  const fetchLookup = async (row) => {
    const attempts = Array.isArray(row?.attempts) ? row.attempts : [];
    const raw = attempts.find((a) => a?._raw)?._raw || row?._raw || {};
    const parentId =
      raw?.parentId ??
      raw?.parent_id ??
      attempts.map((a) => a?._raw?.parentId ?? a?._raw?.parent_id).find((v) => v != null) ??
      null;
    const email = row?.email || null;
    const params = parentId ? { parentId } : email ? { email } : null;
    if (!params) return null;
    const { data } = await http.get("/admin/email-validation/lookup", { params });
    return data ?? null;
  };

  const openDetails = async (row) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsData(null);
    setDetailsRow(row || null);
    try {
      const data = await fetchLookup(row);
      setDetailsData(data);
    } catch (e) {
      setDetailsError(mapHttpErrorToMessage(e, t));
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsLoading(false);
    setDetailsError(null);
    setDetailsData(null);
    setDetailsRow(null);
  };

  const fetchAll = async () => {
    setErr(null);
    setLoading(true);
    try {
      const [notOkRes, currentRes, pendingRes, teacherRes] = await Promise.all([
        http.get("/admin/email-validation/not-ok", { params: { limit } }),
        http.get("/admin/email-validation/current", { params: { limit } }),
        http.get("/admin/email-validation/pending"),
        http
          .get("/admin/teachers/recipients")
          .catch(() =>
            http.get("/admin/newsletter/recipients/maintenance", {
              params: { includeParents: "false", includeTeachers: "true" },
            })
          )
          .catch(() => http.get("/teachers")),
      ]);

      const notOkAttempts = pickItems(notOkRes?.data).map((item) => normalizeAttempt(item, { kind: "not_ok" }));
      const currentAttempts = pickItems(currentRes?.data).map((item) => normalizeAttempt(item, { kind: "current" }));
      const pendingAttempts = pickItems(pendingRes?.data).map((item) =>
        normalizeAttempt(item, { status: "pending", kind: "pending" })
      );

      const merged = [...notOkAttempts, ...currentAttempts, ...pendingAttempts].filter((r) => r && r.email);

      const grouped = new Map();
      merged.forEach((attempt) => {
        const emailKey = String(attempt.email || "").trim().toLowerCase();
        if (!emailKey) return;
        const existing = grouped.get(emailKey);
        if (!existing) grouped.set(emailKey, [attempt]);
        else existing.push(attempt);
      });

      const rowsDeduped = Array.from(grouped.values()).map((attempts) => {
        const sorted = [...attempts].sort((a, b) => {
          const at = a?.checkedAtDate ? a.checkedAtDate.getTime() : -1;
          const bt = b?.checkedAtDate ? b.checkedAtDate.getTime() : -1;
          return bt - at;
        });
        const latestWithDate = sorted.find((a) => a?.checkedAtDate) || null;
        const hasPending = sorted.some((a) => toStatus(a?.status) === "pending" || a?.kind === "pending");
        const base = latestWithDate || sorted[0] || {};
        const pickedFullName = sorted.map((a) => String(a?.fullName || "").trim()).find(Boolean) || "";
        const pickedParentId = sorted.map((a) => String(a?.parentId || "").trim()).find(Boolean) || "";
        const status = base?.status || (hasPending ? "pending" : "");
        return {
          email: base.email || "",
          fullName: pickedFullName,
          parentId: pickedParentId,
          suggestedEmail: base.suggestedEmail || "",
          status,
          reasons: base.reasons,
          checkedAt: base.checkedAt,
          checkedAtDate: base.checkedAtDate,
          attempts: sorted,
        };
      });

      // Add teacher recipients that have no validation attempts yet.
      const teacherList = Array.isArray(teacherRes?.data) ? teacherRes.data : [];
      const teacherEmailSetNext = new Set(
        teacherList
          .map((item) => pickFirst(item?.email, item?.emailAddress, item?.email_address))
          .filter(Boolean)
          .map((v) => String(v).trim().toLowerCase())
          .filter(Boolean)
      );

      const existingEmails = new Set(rowsDeduped.map((r) => String(r?.email || "").trim().toLowerCase()).filter(Boolean));
      teacherEmailSetNext.forEach((email) => {
        if (existingEmails.has(email)) return;
        rowsDeduped.push({
          email,
          fullName: "",
          parentId: "",
          suggestedEmail: "",
          status: "pending",
          reasons: ["no_check_yet"],
          checkedAt: null,
          checkedAtDate: null,
          attempts: [],
        });
      });

      // Sort by most recently checked first; pending-only go last.
      rowsDeduped.sort((a, b) => {
        const at = a?.checkedAtDate ? a.checkedAtDate.getTime() : -1;
        const bt = b?.checkedAtDate ? b.checkedAtDate.getTime() : -1;
        return bt - at;
      });

      setRows(rowsDeduped);
      setTeacherEmailSet(teacherEmailSetNext);
    } catch (e) {
      setErr(mapHttpErrorToMessage(e, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // teacherEmailSet is loaded as part of fetchAll (so it stays in sync with Refresh)

  const filteredItems = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    const statuses = Array.isArray(selectedStatuses) ? selectedStatuses : [];
    return (Array.isArray(rows) ? rows : []).filter((row) => {
      if (statuses.length && !statuses.includes(toStatus(row.status))) return false;
      if (!q) return true;
      return safeString(row).toLowerCase().includes(q);
    });
  }, [rows, query, selectedStatuses]);

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-[240px]">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t("admin.email_validation.checks.title", { defaultValue: "E-mail validatie checks" })}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {t("admin.email_validation.checks.subtitle", {
            defaultValue: "Bekijk onjuiste, huidige en pending e-mailchecks (max 500).",
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("common.search", { defaultValue: "Zoeken…" })}
          className="lumen-glow-input w-[260px] max-w-full px-4 py-2 text-sm"
        />
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="lumen-glow-sheen rounded-full border border-indigo-100/60 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:opacity-60 dark:border-indigo-500/40 dark:bg-indigo-900/40 dark:text-indigo-200"
        >
          {loading ? t("common.loading", { defaultValue: "Loading…" }) : t("common.actions.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>
    </div>
  );

  const statusOptions = useMemo(
    () => [
      { id: "valid", label: t("admin.email_validation.status.valid", { defaultValue: "valid" }) },
      { id: "invalid", label: t("admin.email_validation.status.invalid", { defaultValue: "invalid" }) },
      { id: "suspicious", label: t("admin.email_validation.status.suspicious", { defaultValue: "suspicious" }) },
      { id: "pending", label: t("admin.email_validation.status.pending", { defaultValue: "pending" }) },
    ],
    [t]
  );

  const headerLabelForColumn = (column) => {
    const c = String(column || "");
    switch (c) {
      case "email":
        return t("admin.email_validation.checks.columns.email", { defaultValue: "email" });
      case "fullName":
        return t("admin.email_validation.checks.columns.fullName", { defaultValue: "Naam" });
      case "suggestedEmail":
        return t("admin.email_validation.checks.columns.suggestedEmail", { defaultValue: "suggestedEmail" });
      case "status":
        return t("admin.email_validation.checks.columns.status", { defaultValue: "status" });
      case "reasons":
        return t("admin.email_validation.checks.columns.reasons", { defaultValue: "reasons" });
      case "checkedAt":
        return t("admin.email_validation.checks.columns.checkedAt", { defaultValue: "checkedAt" });
      default:
        return c;
    }
  };

  const renderReasons = (value) => {
    const list = normalizeReasonList(value);
    if (!list.length) return { text: "—", title: undefined };
    const translated = list.map((reason) =>
      t(`admin.email_validation.reason.${reason}`, {
        defaultValue: humanizeReason(reason) || String(reason),
      })
    );
    return { text: translated.join(", "), title: translated.join(" • ") };
  };

  const toggleStatus = (id) => {
    const next = new Set(Array.isArray(selectedStatuses) ? selectedStatuses : []);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedStatuses(Array.from(next));
  };

  const clearStatuses = () => setSelectedStatuses([]);

  const multiFilters = (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
        {t("admin.email_validation.checks.current_filter", { defaultValue: "Filter status" })}:
      </span>
      {selectedStatuses.length === 0 ? (
        <FilterChip active onClick={clearStatuses}>
          {t("common.all", { defaultValue: "Alle" })}
        </FilterChip>
      ) : null}

      {selectedStatuses.length ? (
        <div className="flex flex-wrap items-center gap-2">
          {selectedStatuses.map((id) => {
            const label = statusOptions.find((o) => o.id === id)?.label || id;
            return <StatusPill key={id} label={label} status={id} onRemove={() => toggleStatus(id)} />;
          })}
          <button
            type="button"
            onClick={clearStatuses}
            className="rounded-full border border-slate-200/70 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-white dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
          >
            {t("common.clear", { defaultValue: "Wissen" })}
          </button>
        </div>
      ) : null}

      {statusOptions.map((o) => (
        <FilterChip key={o.id} active={selectedStatuses.includes(o.id)} onClick={() => toggleStatus(o.id)}>
          {o.label}
        </FilterChip>
      ))}
    </div>
  );

  const table = (
    <LdsTableCard className="mt-4">
      <LdsTableScroll>
        <LdsTable>
          <LdsTableHead>
            <LdsTableRow>
              {DISPLAY_COLUMNS.map((c) => (
                <LdsTableHeadCell key={c}>
                  {headerLabelForColumn(c)}
                </LdsTableHeadCell>
              ))}
              <LdsTableHeadCell className="text-right">
                {t("common.actions.name", { defaultValue: "Actions" })}
              </LdsTableHeadCell>
            </LdsTableRow>
          </LdsTableHead>
          <LdsTableBody>
            {loading ? (
              <LdsTableRow>
                <LdsTableCell colSpan={DISPLAY_COLUMNS.length + 1}>
                  <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-200">
                    <Spinner />
                    {t("common.loading", { defaultValue: "Loading…" })}
                  </div>
                </LdsTableCell>
              </LdsTableRow>
            ) : filteredItems.length === 0 ? (
              <LdsTableRow>
                <LdsTableCell className="text-gray-600 dark:text-gray-300" colSpan={DISPLAY_COLUMNS.length + 1}>
                  {t("common.empty", { defaultValue: "No items." })}
                </LdsTableCell>
              </LdsTableRow>
            ) : (
              filteredItems.map((item, idx) => {
                const key = item?.email ? `${item.email}-${idx}` : String(idx);
                return (
                  <LdsTableRow
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetails(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openDetails(item);
                      }
                    }}
                    className="cursor-pointer bg-white/85 transition-colors hover:bg-indigo-50/60 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:bg-gray-900/20 dark:hover:bg-indigo-900/20"
                    aria-label={t("common.details", { defaultValue: "Details" })}
                  >
                    {DISPLAY_COLUMNS.map((c) => (
                      <LdsTableCell key={c}>
                        {c === "status" ? (
                          <StatusBadge
                            status={item?.status}
                            label={statusOptions.find((o) => o.id === toStatus(item?.status))?.label || renderValue(item?.[c])}
                          />
                        ) : c === "email" ? (
                          <div className="flex items-center gap-2">
                            <span className="block max-w-[34ch] truncate" title={typeof item?.[c] === "string" ? item[c] : undefined}>
                              {renderValue(item?.[c])}
                            </span>
                            {teacherEmailSet &&
                              teacherEmailSet.has(String(item?.email || "").trim().toLowerCase()) && (
                                <span className="inline-flex items-center rounded-full border border-indigo-200/70 bg-indigo-50/70 px-2 py-0.5 text-[11px] font-semibold text-indigo-800 dark:border-indigo-500/30 dark:bg-indigo-950/20 dark:text-indigo-200">
                                  {t("common.teacher", { defaultValue: "Docent" })}
                                </span>
                              )}
                          </div>
                        ) : c === "fullName" ? (
                          <span className="block max-w-[28ch] truncate" title={typeof item?.[c] === "string" ? item[c] : undefined}>
                            {renderValue(item?.[c])}
                          </span>
                        ) : c === "reasons" ? (
                          (() => {
                            const rendered = renderReasons(item?.[c]);
                            return (
                              <span className="block max-w-[40ch] truncate" title={rendered.title}>
                                {rendered.text}
                              </span>
                            );
                          })()
                        ) : c === "checkedAt" ? (
                          <span className="block max-w-[22ch] truncate" title={safeString(item?.[c]) || undefined}>
                            {formatDateTime(item?.[c])}
                          </span>
                        ) : (
                          <span
                            className={`block truncate ${
                              c === "email" ? "max-w-[34ch]" : c === "reasons" ? "max-w-[40ch]" : "max-w-[22ch]"
                            }`}
                            title={typeof item?.[c] === "string" ? item[c] : undefined}
                          >
                            {renderValue(item?.[c])}
                          </span>
                        )}
                      </LdsTableCell>
                    ))}
                    <LdsTableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {String(item?.parentId || "").trim() ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/family/parent/${encodeURIComponent(item.parentId)}/details`);
                            }}
                            className="rounded-full border border-slate-200/70 bg-white/70 px-3 py-1 text-sm font-semibold text-slate-700 transition hover:bg-white hover:text-slate-900 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
                          >
                            {t("common.open_parent", { defaultValue: "Open ouder" })}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openDetails(item);
                          }}
                          className="rounded-full border border-indigo-200/60 bg-indigo-50/70 px-3 py-1 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-900/30 dark:text-indigo-200"
                        >
                          {t("common.details", { defaultValue: "Details" })}
                        </button>
                      </div>
                    </LdsTableCell>
                  </LdsTableRow>
                );
              })
            )}
          </LdsTableBody>
        </LdsTable>
      </LdsTableScroll>
    </LdsTableCard>
  );

  return (
    <div className={className}>
      {!embedded && header}
      {embedded ? (
        <div className="rounded-2xl border border-indigo-100/60 bg-white/90 p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/60">
          {header}
          {multiFilters}
          {err && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
              {err?.message || err?.title || err}
            </div>
          )}
          {table}
        </div>
      ) : (
        <>
          {multiFilters}
          {err && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
              {err?.message || err?.title || err}
            </div>
          )}
          {table}
        </>
      )}

      {detailsOpen && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={closeDetails}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-[560px] overflow-auto bg-white shadow-2xl dark:bg-gray-950">
            <div className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/95 p-4 backdrop-blur dark:border-slate-800/70 dark:bg-gray-950/90">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                  {(() => {
                    const fromDetails =
                      detailsData?.fullName ||
                      detailsData?.full_name ||
                      detailsData?.displayName ||
                      detailsData?.display_name ||
                      detailsData?.name ||
                      detailsData?.parentFullName ||
                      detailsData?.parent_full_name ||
                      detailsData?.parentName ||
                      detailsData?.parent_name ||
                      "";
                    const fromRow = detailsRow?.fullName || "";
                    const resolved = String(fromDetails || fromRow || "").trim();
                    return resolved || t("admin.email_validation.checks.drawer.title", { defaultValue: "Laatste check" });
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const parentId =
                      detailsData?.parentId ||
                      detailsData?.parent_id ||
                      detailsData?.parentUUID ||
                      detailsData?.parentUuid ||
                      detailsData?.parent_uuid ||
                      detailsRow?._raw?.parentId ||
                      detailsRow?._raw?.parent_id ||
                      detailsRow?.parentId ||
                      "";
                    const resolved = String(parentId || "").trim();
                    if (!resolved) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => navigate(`/family/parent/${encodeURIComponent(resolved)}/details`)}
                        className="rounded-full border border-slate-200/70 bg-white px-3 py-1 text-sm font-semibold text-slate-700 transition hover:bg-white hover:text-slate-900 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
                      >
                        {t("common.open_parent", { defaultValue: "Open ouder" })}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={closeDetails}
                    className="rounded-full border border-slate-200/70 bg-white px-3 py-1 text-sm font-semibold text-slate-700 transition hover:text-slate-900 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
                  >
                    {t("common.close", { defaultValue: "Close" })}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {detailsLoading ? (
                <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-200">
                  <Spinner />
                  {t("common.loading", { defaultValue: "Loading…" })}
                </div>
              ) : detailsError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
                  {detailsError?.message || detailsError?.title || detailsError}
                </div>
              ) : !detailsData ? (
                <div className="text-sm text-slate-600 dark:text-slate-200">
                  {t("common.empty", { defaultValue: "No items." })}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.currentEmail", { defaultValue: "Current email" })}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {detailsData.currentEmail || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.checkedEmail", { defaultValue: "Checked email" })}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {detailsData.checkedEmail || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                            {t("admin.email_validation.checks.drawer.status", { defaultValue: "Status" })}
                          </div>
                          <div className="mt-1">
                            <StatusBadge
                              status={detailsData.status}
                              label={
                                statusOptions.find((o) => o.id === toStatus(detailsData.status))?.label ||
                                safeString(detailsData.status) ||
                                "—"
                              }
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                            {t("admin.email_validation.checks.drawer.checkedAt", { defaultValue: "Checked at" })}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {detailsData.checkedAt ? formatDateTime(detailsData.checkedAt) : "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.reasons", { defaultValue: "Reasons" })}
                      </div>
                      <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                        {renderReasons(detailsData.reasons).text}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.suggestedEmail", { defaultValue: "Suggested email" })}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {detailsData.suggestedEmail || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.source", { defaultValue: "Source" })}
                      </div>
                      <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                        {detailsData.source || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.matchesCurrent", {
                          defaultValue: "Checked email matches current",
                        })}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {detailsData.checkedEmailMatchesCurrentEmail === true
                          ? t("common.yes", { defaultValue: "Ja" })
                          : detailsData.checkedEmailMatchesCurrentEmail === false
                            ? t("common.no", { defaultValue: "Nee" })
                            : "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("admin.email_validation.checks.drawer.pending", { defaultValue: "Pending" })}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {detailsData.pending === true
                          ? t("common.yes", { defaultValue: "Ja" })
                          : detailsData.pending === false
                            ? t("common.no", { defaultValue: "Nee" })
                            : "—"}
                      </div>
                    </div>
                  </div>

                  {Array.isArray(detailsRow?.attempts) && detailsRow.attempts.length ? (
                    <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                      <div className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                        {t("admin.email_validation.checks.drawer.history", { defaultValue: "Historie" })}
                      </div>
                      <LdsTableCard className="mt-3 rounded-xl">
                        <LdsTableScroll>
                          <LdsTable>
                            <LdsTableHead>
                              <LdsTableRow>
                                <LdsTableHeadCell className="p-2">
                                  {t("admin.email_validation.checks.drawer.history_kind", { defaultValue: "Type" })}
                                </LdsTableHeadCell>
                                <LdsTableHeadCell className="p-2">
                                  {t("admin.email_validation.checks.drawer.status", { defaultValue: "Status" })}
                                </LdsTableHeadCell>
                                <LdsTableHeadCell className="p-2">
                                  {t("admin.email_validation.checks.drawer.checkedAt", { defaultValue: "Checked at" })}
                                </LdsTableHeadCell>
                                <LdsTableHeadCell className="p-2">
                                  {t("admin.email_validation.checks.drawer.reasons", { defaultValue: "Reasons" })}
                                </LdsTableHeadCell>
                                <LdsTableHeadCell className="p-2">
                                  {t("admin.email_validation.checks.drawer.suggestedEmail", { defaultValue: "Suggested email" })}
                                </LdsTableHeadCell>
                              </LdsTableRow>
                            </LdsTableHead>
                            <LdsTableBody>
                              {detailsRow.attempts.map((a, i) => (
                                <LdsTableRow key={`${a?.kind || "attempt"}-${i}`} className="align-top">
                                  <LdsTableCell className="p-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                                    {t(`admin.email_validation.checks.drawer.kind.${a?.kind || "unknown"}`, {
                                      defaultValue: a?.kind || "unknown",
                                    })}
                                  </LdsTableCell>
                                  <LdsTableCell className="p-2">
                                    <StatusBadge
                                      status={a?.status}
                                      label={
                                        statusOptions.find((o) => o.id === toStatus(a?.status))?.label ||
                                        safeString(a?.status) ||
                                        "—"
                                      }
                                    />
                                  </LdsTableCell>
                                  <LdsTableCell className="p-2 text-xs font-semibold text-slate-600 dark:text-slate-200">
                                    {a?.checkedAt ? formatDateTime(a.checkedAt) : "—"}
                                  </LdsTableCell>
                                  <LdsTableCell className="p-2 text-sm text-slate-900 dark:text-slate-100">
                                    {renderReasons(a?.reasons).text}
                                  </LdsTableCell>
                                  <LdsTableCell className="p-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {a?.suggestedEmail || "—"}
                                  </LdsTableCell>
                                </LdsTableRow>
                              ))}
                            </LdsTableBody>
                          </LdsTable>
                        </LdsTableScroll>
                      </LdsTableCard>
                    </div>
                  ) : null}

                  <details className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                    <summary className="cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {t("admin.email_validation.checks.drawer.raw", { defaultValue: "Raw response" })}
                    </summary>
                    <pre className="mt-3 max-h-[40vh] overflow-auto rounded-xl bg-slate-950/90 p-4 text-xs text-slate-100">
                      {safeString(detailsData)}
                    </pre>
                  </details>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default EmailValidationChecksPanel;
